import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { api } from "@/services/api";
import type { PublicDeskReport } from "@/types";

/** The Dominion Programme page on the marketing website. */
const DOMINION_URL =
  import.meta.env.VITE_DOMINION_URL || "https://dominionfinancialreview.com/dominion";

type LoadState =
  | { status: "loading" }
  | { status: "missing" }
  | { status: "ready"; report: PublicDeskReport };

function Stars({ rating }: { rating: number }) {
  return (
    <div role="img" aria-label={`${rating} out of 5 stars`} className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <svg
          key={n}
          viewBox="0 0 24 24"
          className="size-5"
          fill={n <= rating ? "#c2986a" : "none"}
          stroke="#c2986a"
          strokeWidth={1.6}
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z" />
        </svg>
      ))}
    </div>
  );
}

function LinkedinIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="currentColor" aria-hidden="true">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

/** A published Desk review's Report, public and without login (KC-510). */
export default function PublicDeskReportPage() {
  const { reviewId = "" } = useParams();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    api.getPublicDeskReport(reviewId).then((report) => {
      if (!cancelled) setState(report ? { status: "ready", report } : { status: "missing" });
    });
    return () => {
      cancelled = true;
    };
  }, [reviewId]);

  return (
    <div className="min-h-screen bg-[#F2EFE3] text-[#013220] font-[Manrope,system-ui,sans-serif]">
      <main className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
        {state.status === "loading" && <p className="text-sm opacity-70">Loading…</p>}
        {state.status === "missing" && (
          <p className="text-lg font-semibold">This report isn't available.</p>
        )}
        {state.status === "ready" && <Report report={state.report} />}
        <footer className="mt-12 border-t border-[#e3dccb] pt-6">
          <a
            href={DOMINION_URL}
            className="font-bold text-[#013220] underline-offset-4 hover:underline"
          >
            About the Dominion Programme →
          </a>
        </footer>
      </main>
    </div>
  );
}

function Report({ report }: { report: PublicDeskReport }) {
  const subtitle = [report.role, report.firm].filter(Boolean).join(" · ");
  return (
    <article className="min-w-0">
      <p className="text-[13px] font-bold uppercase tracking-[3px] text-[#785c2f]">FROM THE DESK</p>
      <h1 className="mt-3 text-[32px] font-extrabold leading-tight break-words">
        {report.topic_title ?? "Desk report"}
      </h1>

      <div className="mt-6 flex items-center gap-4">
        {report.photo_url ? (
          <img
            src={report.photo_url}
            alt=""
            className="size-14 shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-[#e3dccb] font-bold">
            {initials(report.display_name)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-bold">{report.display_name}</p>
          {subtitle && <p className="text-sm opacity-80">{subtitle}</p>}
        </div>
        {report.linkedin_url && (
          <a
            href={report.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${report.display_name} on LinkedIn`}
            className="shrink-0 text-[#013220] hover:opacity-70"
          >
            <LinkedinIcon />
          </a>
        )}
      </div>

      <figure className="mt-6 rounded-2xl border border-[#e3dccb] bg-[#FDF6EC] p-5">
        <Stars rating={report.rating} />
        {report.comment && (
          <blockquote className="mt-3 text-base leading-relaxed break-words">
            “{report.comment}”
          </blockquote>
        )}
      </figure>

      <section className="mt-8">
        {report.pdf_url ? (
          <iframe
            src={`${report.pdf_url}#navpanes=0`}
            title={`Report by ${report.display_name}`}
            className="h-[75vh] min-h-[480px] w-full rounded-2xl border border-[#e3dccb] bg-white"
          />
        ) : (
          <p className="text-sm opacity-70">The report file is not available.</p>
        )}
      </section>
    </article>
  );
}
